/**
 * Renders a single waterbody citation. Built and inserted
 * directly by `citation-markdown-plugin.ts`'s rehype transform as part of
 * the markdown-to-HTML pipeline -- since that transform only ever produces
 * a HAST node that gets serialized to an HTML *string*, this element is
 * populated from its own `data-*` attributes rather than a live property
 * (a JS object/function reference cannot survive that serialization round
 * trip the way it could when a hydration pass used to construct this
 * element directly via `document.createElement` + property assignment).
 *
 * Owns its own click-to-navigate: unlike the portable chat-card machinery,
 * this component has always been fish-specific, so it imports fish's own
 * `navigateToWaterbody` directly rather than needing a dependency-injected
 * callback.
 */

import { navigateToWaterbody } from '@/fish/lib/ai/navigation'

export const TAG_NAME = 'fish-citation-waterbody'

const styles = `
  :host {
    display: block;
  }
  :host([hidden]) {
    display: none;
  }
  .item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ys-spacing-150, 12px);
    width: 100%;
    padding: var(--ys-spacing-150, 12px) var(--ys-spacing-200, 16px);
    box-sizing: border-box;
    border: 1px solid var(--ys-color-stroke-primary, #d0d0d0);
    border-radius: var(--ys-radius-200, 8px);
    background: var(--ys-color-background-primary, #fff);
    color: var(--ys-color-text-primary, #111);
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }
  .item:hover,
  .item:focus-visible {
    cursor: pointer;
    background: var(--ys-color-surface-base, #f5f5f5);
    border-color: var(--ys-color-stroke-reverse, #999);
  }
  .item:focus-visible {
    outline: 2px solid var(--ys-color-stroke-reverse, #000);
    outline-offset: 2px;
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--ys-spacing-25, 2px);
    min-width: 0;
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    color: var(--ys-color-text-dim, #666);
  }
  .chevron {
    flex: 0 0 auto;
    color: var(--ys-color-icon-primary, #666);
    font-size: 1.1em;
    line-height: 1;
  }

  /* Yellowstone type styles (see @onxmaps/ys-tokens/dist/type-styles.css) --
     inlined rather than adopting the external stylesheet, since these are
     the only two type styles this component needs and shadow DOM doesn't
     pick up global class rules automatically. The --ys-text-* custom
     properties themselves are global and already loaded app-wide. */
  .ys-text-title4 {
    font-family: var(--ys-text-title4-fontFamily);
    font-weight: var(--ys-text-title4-fontWeight);
    line-height: var(--ys-text-title4-lineHeight);
    font-size: var(--ys-text-title4-fontSize);
    letter-spacing: var(--ys-text-title4-letterSpacing);
  }
  .ys-text-metadata1 {
    font-family: var(--ys-text-metadata1-fontFamily);
    font-weight: var(--ys-text-metadata1-fontWeight);
    line-height: var(--ys-text-metadata1-lineHeight);
    font-size: var(--ys-text-metadata1-fontSize);
    letter-spacing: var(--ys-text-metadata1-letterSpacing);
  }
`

const titleCase = (slug: string): string =>
  slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

interface PotentialMatch {
  category: string
  label: string
  species: string[]
}

const parsePotentialMatches = (raw: string | null): PotentialMatch[] => {
  if (!raw) {
    return []
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as PotentialMatch[]) : []
  } catch {
    return []
  }
}

export class FishCitationWaterbody extends HTMLElement {
  static get observedAttributes(): string[] {
    return [
      'data-name',
      'data-distance-text',
      'data-direction',
      'data-potential',
    ]
  }

  private _item: HTMLElement

  constructor() {
    super()
    const shadow = this.attachShadow({ mode: 'open' })
    const style = document.createElement('style')
    style.textContent = styles
    const item = document.createElement('button')
    item.type = 'button'
    item.className = 'item'
    item.addEventListener('click', () => this._handleClick())
    shadow.append(style, item)
    this._item = item
  }

  connectedCallback(): void {
    this._render()
  }

  attributeChangedCallback(): void {
    this._render()
  }

  private _handleClick(): void {
    const id = this.getAttribute('data-id')
    if (id) {
      navigateToWaterbody(id)
    }
  }

  private _potentialLines(): string[] {
    return parsePotentialMatches(this.getAttribute('data-potential')).map(
      (match) => `${match.label}: ${match.species.map(titleCase).join(', ')}`
    )
  }

  private _render(): void {
    this._item.textContent = ''
    const name = this.getAttribute('data-name')
    if (!name) {
      return
    }

    const body = document.createElement('span')
    body.className = 'body'

    const nameEl = document.createElement('span')
    nameEl.className = 'ys-text-title4 name'
    nameEl.textContent = name
    body.appendChild(nameEl)

    const distanceText = this.getAttribute('data-distance-text')
    const direction = this.getAttribute('data-direction')
    if (distanceText && direction) {
      const distance = document.createElement('span')
      distance.className = 'ys-text-metadata1 meta'
      distance.textContent = `${distanceText} ${direction}`
      body.appendChild(distance)
    }

    this._potentialLines().forEach((line) => {
      const potential = document.createElement('span')
      potential.className = 'ys-text-metadata1 meta'
      potential.textContent = line
      body.appendChild(potential)
    })

    const chevron = document.createElement('span')
    chevron.className = 'chevron'
    chevron.setAttribute('aria-hidden', 'true')
    chevron.textContent = '›'

    this._item.append(body, chevron)
  }
}

if (!customElements.get(TAG_NAME)) {
  customElements.define(TAG_NAME, FishCitationWaterbody)
}
